import {
  CLERK_PASSWORD_VALIDATION_ERROR_NAME,
  ClerkPasswordValidationError,
} from '@lib/clerk/errors/clerk-password-validation-error';
import { useMutation } from '@lib/query/client';

interface VerifyCodeParams {
  email: string;
  code: string;
  /**
   * Optional visitor-typed full name. The server attaches it to the
   * Clerk user only after the OTP succeeds, and only when the
   * account doesn't already have a firstName/lastName — so existing
   * identities are never overwritten.
   */
  fullName?: string;
  firstName?: string;
  lastName?: string;
  /** Promotes a passwordless shell after OTP proof (signup different-email). */
  shellClaim?: {
    firstName: string;
    lastName: string;
    password: string;
  };
}

interface VerifyCodeResponse {
  success: boolean;
  signInToken?: string;
  error?: string;
  /** Machine-readable failure reason for inline handling at call sites. */
  reason?: 'invalid_code' | 'password_validation';
}

// Shared by the class and the Sentry-suppress meta below,
// so the two can never drift out of sync.
const VERIFICATION_CODE_ERROR_NAME = 'VerificationCodeError';

/**
 * Wrong or expired OTP — expected user behavior shown inline
 * at the call site, not an app fault. Named so the global
 * MutationCache skips it from Sentry via
 * `meta.suppressSentryErrorNames` (PORTAL-J8). Other failures
 * stay plain `Error`s and still report.
 */
export class VerificationCodeError extends Error {
  constructor(message: string) {
    super(message);
    this.name = VERIFICATION_CODE_ERROR_NAME;
  }
}

async function verifyCode(
  params: VerifyCodeParams
): Promise<VerifyCodeResponse> {
  const response = await fetch('/api/auth/verification/email/verify-code', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      email: params.email,
      code: params.code,
      ...(params.fullName ? { fullName: params.fullName } : {}),
      ...(params.firstName ? { firstName: params.firstName } : {}),
      ...(params.lastName ? { lastName: params.lastName } : {}),
      ...(params.shellClaim ? { shellClaim: params.shellClaim } : {}),
    }),
  });

  const data: VerifyCodeResponse = await response.json();

  if (!response.ok) {
    const message = data.error || 'Verification failed';
    if (data.reason === 'invalid_code') {
      throw new VerificationCodeError(message);
    }
    if (data.reason === 'password_validation') {
      throw new ClerkPasswordValidationError(message);
    }
    throw new Error(message);
  }

  return data;
}

export function useVerifyCode() {
  return useMutation({
    mutationFn: verifyCode,
    // Wrong/expired code is user behavior shown inline, not a
    // fault to page on. Other failures throw plain Errors and
    // still report.
    meta: {
      suppressSentryErrorNames: [
        VERIFICATION_CODE_ERROR_NAME,
        CLERK_PASSWORD_VALIDATION_ERROR_NAME,
      ],
    },
  });
}
